import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowSync20Regular,
  ArrowClockwise20Regular,
  Delete20Regular,
  Search20Regular,
  Mail20Regular,
  Eye20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { Modal } from "../../components/Modal";
import { DocumentViewer, type ViewerDocument } from "../../components/DocumentViewer";
import { Tooltip } from "../../components/Tooltip";
import { APP_LOCALE } from "../../config/locale";
import {
  ApiError,
  getEmailImports,
  getEmailImportBody,
  dismissAllSkippedAttachments,
  dismissDiscardedDocument,
  dismissSkippedAttachments,
  getDiscardedDocuments,
  fetchFinancialDocument,
  reclassifyDocument,
  reprocessEmailInvoices,
  resetEmailImports,
  type EmailImport,
  type AttentionSummary,
  type DiscardedDocument,
} from "../../faturas/faturasApi";

// Aparência de cada estado. Cores sólidas via style inline (as variantes de opacidade
// sólidas do Tailwind não são compiladas — só os tokens do tema e /10../30 usados no resto do ecrã).
const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  Imported: { label: "Importado", color: "#15803d", bg: "rgba(34,197,94,0.14)" },
  NoAttachments: { label: "Sem anexos", color: "#6b7280", bg: "rgba(107,114,128,0.14)" },
  PartiallyFailed: { label: "Parcial", color: "#b45309", bg: "rgba(245,158,11,0.16)" },
  Failed: { label: "Falhou", color: "#b91c1c", bg: "rgba(239,68,68,0.14)" },
  SenderBlocked: { label: "Remetente bloqueado", color: "#b45309", bg: "rgba(245,158,11,0.16)" },
};

const TO_REVIEW = "por-rever";

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "Todos os estados" },
  { value: "Imported", label: "Importado" },
  { value: "PartiallyFailed", label: "Parcial" },
  { value: "Failed", label: "Falhou" },
  { value: "NoAttachments", label: "Sem anexos" },
  { value: "SenderBlocked", label: "Remetente bloqueado" },
  // Não é um estado do servidor: pede os emails com anexos ignorados ainda por rever.
  { value: TO_REVIEW, label: "Anexos ignorados por rever" },
];

const DANGER = "#b91c1c";

const formatDateTime = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString(APP_LOCALE);
};

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) return "Sessão expirada. Autentique-se novamente.";
    if (error.status === 403) return "Sem permissões para ver as importações por email.";
    if (error.message) {
      // O corpo de um erro do servidor costuma ser um ProblemDetails JSON — extrai a
      // mensagem legível em vez de mostrar o JSON cru.
      try {
        const parsed = JSON.parse(error.message);
        if (parsed && typeof parsed === "object") {
          return parsed.detail || parsed.title || error.message;
        }
      } catch {
        // Não é JSON — usa o texto tal como veio.
      }
      return error.message;
    }
  }
  return "Não foi possível carregar as importações por email.";
};

function StatusBadge({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? {
    label: status,
    color: "#6b7280",
    bg: "rgba(107,114,128,0.14)",
  };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-xs rounded font-medium"
      style={{ color: meta.color, backgroundColor: meta.bg }}
    >
      {meta.label}
    </span>
  );
}

export function ImportacoesEmailView() {
  const [imports, setImports] = useState<EmailImport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  // Descartados: documentos importados que o classificador considerou "não parece fatura".
  const [discarded, setDiscarded] = useState<DiscardedDocument[]>([]);
  const [recoveringId, setRecoveringId] = useState<number | null>(null);
  // Linha a ser dispensada ("doc-<id>" ou "email-<id>"): uma de cada vez, sem duplo clique.
  const [dismissingKey, setDismissingKey] = useState<string | null>(null);
  const discardedSectionRef = useRef<HTMLDivElement>(null);
  // Os números "por decidir" vêm do servidor, os mesmos do menu: a lista só carrega as linhas
  // mais recentes e está sujeita à pesquisa, por isso contá-la daria outro número.
  const [reviewCounts, setReviewCounts] = useState<AttentionSummary | null>(null);
  const [confirmDismissAll, setConfirmDismissAll] = useState(false);

  // O ShellLayout pede o resumo ao servidor e difunde-o ("aja2:attention-summary"): este ecrã só
  // pede a atualização e ouve a resposta, em vez de fazer o mesmo pedido outra vez.
  const refreshReviewCounts = useCallback(() => {
    window.dispatchEvent(new Event("aja2:attention"));
  }, []);

  useEffect(() => {
    const onSummary = (event: Event) => {
      setReviewCounts((event as CustomEvent<AttentionSummary>).detail ?? null);
    };
    window.addEventListener("aja2:attention-summary", onSummary);
    return () => window.removeEventListener("aja2:attention-summary", onSummary);
  }, []);
  // Descartado aberto no viewer: é vendo o ficheiro que se decide se é para recuperar.
  const [viewerDocument, setViewerDocument] = useState<ViewerDocument | null>(null);

  // Pré-visualização do email: guarda o item + o HTML carregado (ou um erro).
  const [emailModal, setEmailModal] = useState<{
    item: EmailImport;
    html: string | null;
    error: string | null;
  } | null>(null);
  const [viewingId, setViewingId] = useState<number | null>(null);

  // Reprocessamento do lote importado por email (reclassifica despesa/receita).
  const [confirmReprocess, setConfirmReprocess] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessBanner, setReprocessBanner] = useState<string | null>(null);

  const runReprocess = useCallback(async () => {
    setReprocessing(true);
    setReprocessBanner(null);
    try {
      const result = await reprocessEmailInvoices();
      const skipped =
        result.skippedValidated > 0
          ? ` ${result.skippedValidated} já validado(s) foram ignorados.`
          : "";
      setReprocessBanner(
        `${result.queued} documento(s) em reprocessamento.${skipped} As faturas reclassificadas aparecem em Faturas Receitas / Despesas dentro de instantes.`,
      );
    } catch (error) {
      setReprocessBanner(parseErrorMessage(error));
    } finally {
      setReprocessing(false);
      setConfirmReprocess(false);
    }
  }, []);

  // Reset: apagar os dados da importação por email para reimportar do zero.
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  const openEmail = useCallback(async (item: EmailImport) => {
    setViewingId(item.id);
    try {
      const html = await getEmailImportBody(item.id);
      setEmailModal({ item, html, error: null });
    } catch (error) {
      setEmailModal({ item, html: null, error: parseErrorMessage(error) });
    } finally {
      setViewingId(null);
    }
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [result, discardedResult] = await Promise.all([
        getEmailImports(
          status === TO_REVIEW
            ? { search, toReview: true, pageSize: 200 }
            : { search, status, pageSize: 200 },
        ),
        getDiscardedDocuments({ search, pageSize: 200 }),
      ]);
      setImports(result.items);
      setDiscarded(discardedResult.items);
      refreshReviewCounts();
    } catch (error) {
      setImports([]);
      setDiscarded([]);
      setLoadError(parseErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [search, status, refreshReviewCounts]);

  useEffect(() => {
    const handle = setTimeout(load, 250);
    return () => clearTimeout(handle);
  }, [load]);

  const runReset = useCallback(async () => {
    setResetting(true);
    setReprocessBanner(null);
    try {
      const result = await resetEmailImports();
      const skipped =
        result.skippedValidated > 0
          ? ` ${result.skippedValidated} já validado(s) foram mantidos.`
          : "";
      setReprocessBanner(
        `Apagados ${result.documentsDeleted} documento(s) e ${result.messagesDeleted} registo(s) de importação.${skipped} Remove a etiqueta AJA2/Importado no Gmail e reinicia a API para reimportar.`,
      );
      await load();
    } catch (error) {
      setReprocessBanner(parseErrorMessage(error));
    } finally {
      setResetting(false);
      setConfirmReset(false);
    }
  }, [load]);

  const runRecover = useCallback(async (doc: DiscardedDocument) => {
    setRecoveringId(doc.id);
    setReprocessBanner(null);
    try {
      await reclassifyDocument(doc.id);
      setDiscarded((current) => current.filter((item) => item.id !== doc.id));
      // O número do menu conta estas linhas: desce na hora.
      refreshReviewCounts();
      setReprocessBanner(
        `"${doc.fileName}" marcado como fatura — vai aparecer "por validar" em Faturas Despesas / Receitas dentro de instantes.`,
      );
    } catch (error) {
      setReprocessBanner(parseErrorMessage(error));
    } finally {
      setRecoveringId(null);
    }
  }, [refreshReviewCounts]);

  // Dispensar = "vi, não é fatura": a linha sai da lista e do número do menu. O ficheiro não se
  // apaga — apagá-lo deixava a importação por email trazê-lo de volta como novo.
  const runDismissDiscarded = useCallback(async (doc: DiscardedDocument) => {
    setDismissingKey(`doc-${doc.id}`);
    setReprocessBanner(null);
    try {
      await dismissDiscardedDocument(doc.id);
      setDiscarded((current) => current.filter((item) => item.id !== doc.id));
      refreshReviewCounts();
    } catch (error) {
      setReprocessBanner(parseErrorMessage(error));
    } finally {
      setDismissingKey(null);
    }
  }, [refreshReviewCounts]);

  // Anexos ignorados de um email dados como vistos: o email fica na lista, deixa de contar.
  const runDismissSkipped = useCallback(async (item: EmailImport) => {
    setDismissingKey(`email-${item.id}`);
    setReprocessBanner(null);
    try {
      await dismissSkippedAttachments(item.id);
      setImports((current) =>
        current.map((row) => (row.id === item.id ? { ...row, skippedDismissed: true } : row)),
      );
      refreshReviewCounts();
    } catch (error) {
      setReprocessBanner(parseErrorMessage(error));
    } finally {
      setDismissingKey(null);
    }
  }, [refreshReviewCounts]);

  // Todos os emails com anexos ignorados por rever, de uma vez (quase todos são logótipos).
  const runDismissAllSkipped = useCallback(async () => {
    setDismissingKey("email-all");
    setReprocessBanner(null);
    try {
      const dismissed = await dismissAllSkippedAttachments();
      setImports((current) =>
        current.map((row) => (row.skippedAttachmentCount > 0 ? { ...row, skippedDismissed: true } : row)),
      );
      refreshReviewCounts();
      setReprocessBanner(`${dismissed} email(s) com anexos ignorados dados como vistos.`);
    } catch (error) {
      setReprocessBanner(parseErrorMessage(error));
    } finally {
      setDismissingKey(null);
      setConfirmDismissAll(false);
    }
  }, [refreshReviewCounts]);

  const summary = useMemo(() => {
    const docs = imports.reduce((sum, item) => sum + item.importedCount, 0);
    const failed = imports.filter(
      (item) => item.status === "Failed" || item.status === "PartiallyFailed",
    ).length;
    return { emails: imports.length, docs, failed };
  }, [imports]);

  const actions = (
    <div className="flex items-center gap-2">
      <button
        className="px-4 py-2 border border-border rounded transition-colors flex items-center gap-2 hover:bg-accent/30 disabled:opacity-50"
        style={{ borderRadius: "var(--radius-button)", height: "var(--button-height)" }}
        onClick={() => setConfirmReprocess(true)}
        disabled={reprocessing}
        title="Voltar a correr a extração/classificação sobre os documentos importados por email"
      >
        <ArrowClockwise20Regular className="w-5 h-5" />
        {reprocessing ? "A reprocessar…" : "Reprocessar"}
      </button>
      <button
        className="px-4 py-2 border border-border rounded transition-colors flex items-center gap-2 hover:bg-accent/30 disabled:opacity-50"
        style={{ borderRadius: "var(--radius-button)", height: "var(--button-height)" }}
        onClick={load}
        disabled={isLoading}
      >
        <ArrowSync20Regular className="w-5 h-5" />
        Atualizar
      </button>
      <button
        className="px-4 py-2 border rounded transition-colors flex items-center gap-2 hover:bg-accent/30 disabled:opacity-50"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
          borderColor: "var(--destructive)",
          color: "var(--destructive)",
        }}
        onClick={() => setConfirmReset(true)}
        disabled={resetting}
        title="Apagar os dados da importação por email para reimportar do zero"
      >
        <Delete20Regular className="w-5 h-5" />
        {resetting ? "A apagar…" : "Apagar tudo"}
      </button>
    </div>
  );

  return (
    <>
    <div className="flex flex-col h-full">
      <PageHeader
        title="Importações por email"
        subtitle="Registo das faturas recebidas na caixa faturas@aja2.pt e injetadas automaticamente para validação."
        actions={actions}
      />

      <div className="p-4 border-b border-border bg-card flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search20Regular className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Procurar por remetente ou assunto…"
            className="w-full pl-10 pr-4 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
            style={{ borderRadius: "var(--radius-button)" }}
          />
        </div>

        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          className="px-3 py-2 border border-border rounded bg-background text-foreground"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          {STATUS_FILTERS.map((filter) => (
            <option key={filter.value} value={filter.value}>
              {filter.label}
            </option>
          ))}
        </select>

        <div className="ml-auto flex items-center gap-2 text-sm text-secondary-text">
          <span>{summary.emails} emails</span>
          <span aria-hidden>·</span>
          <span>{summary.docs} documentos</span>
          {summary.failed > 0 && (
            <>
              <span aria-hidden>·</span>
              <span style={{ color: DANGER }}>{summary.failed} com falha</span>
            </>
          )}
          {discarded.length > 0 && (
            <>
              <span aria-hidden>·</span>
              <span style={{ color: "#b45309" }}>{discarded.length} descartados</span>
            </>
          )}
        </div>
      </div>

      {/* O que o número do menu está a contar e o que fazer a cada linha — a pergunta era
          precisamente "o que é preciso fazer aqui?". */}
      {reviewCounts && reviewCounts.importsToReview > 0 && (
        <div
          className="px-4 py-2 border-b border-border text-sm flex flex-wrap items-center gap-2"
          style={{ backgroundColor: "rgba(180,83,9,0.08)" }}
        >
          <span className="font-medium" style={{ color: "#b45309" }}>
            {reviewCounts.importsToReview} por decidir
          </span>
          {reviewCounts.discardedToReview > 0 && (
            <button
              type="button"
              className="hover:underline text-left"
              style={{ color: "var(--primary)" }}
              onClick={() =>
                discardedSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
            >
              {reviewCounts.discardedToReview}{" "}
              {reviewCounts.discardedToReview === 1 ? "documento descartado" : "documentos descartados"} (no
              fim da lista): recupera o que for fatura ou auto, dispensa o resto
            </button>
          )}
          {reviewCounts.skippedToReview > 0 && (
            <>
              <span className="text-secondary-text">
                {reviewCounts.skippedToReview}{" "}
                {reviewCounts.skippedToReview === 1 ? "email" : "emails"} com anexos ignorados (quase
                sempre logótipos e assinaturas): se faltar uma fatura, reenvia-a; senão dispensa
              </span>
              {status !== TO_REVIEW && (
                <button
                  type="button"
                  className="hover:underline"
                  style={{ color: "var(--primary)" }}
                  onClick={() => setStatus(TO_REVIEW)}
                >
                  Ver só estes
                </button>
              )}
              <button
                type="button"
                className="hover:underline disabled:opacity-50"
                style={{ color: "var(--primary)" }}
                onClick={() => setConfirmDismissAll(true)}
                disabled={dismissingKey === "email-all"}
              >
                Dispensar todos
              </button>
            </>
          )}
        </div>
      )}

      {reprocessBanner && (
        <div className="px-4 py-2 border-b border-border bg-accent/30 text-sm flex items-center justify-between gap-3">
          <span>{reprocessBanner}</span>
          <button
            type="button"
            onClick={() => setReprocessBanner(null)}
            aria-label="Fechar"
            className="text-secondary-text hover:text-foreground px-2"
          >
            ✕
          </button>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {isLoading && (
          <div className="p-8 text-center text-secondary-text">A carregar…</div>
        )}

        {!isLoading && loadError && (
          <div className="p-8 text-center" style={{ color: "var(--destructive)" }}>
            {loadError}
          </div>
        )}

        {!isLoading && !loadError && imports.length === 0 && (
          <div className="p-8 text-center text-secondary-text">
            Ainda não há importações por email. Assim que chegar uma fatura a
            faturas@aja2.pt, aparece aqui e o documento fica por validar em Faturas.
          </div>
        )}

        {!isLoading && !loadError && imports.length > 0 && (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card border-b border-border">
              <tr className="text-left text-secondary-text">
                <th className="px-3 py-2 font-medium whitespace-nowrap">Processado</th>
                <th className="px-3 py-2 font-medium">Remetente</th>
                <th className="px-3 py-2 font-medium">Assunto</th>
                <th className="px-3 py-2 font-medium text-center">Anexos</th>
                <th className="px-3 py-2 font-medium text-center">Importados</th>
                <th className="px-3 py-2 font-medium">Estado</th>
                <th className="px-3 py-2 font-medium text-center" style={{ width: 90 }}>Email</th>
              </tr>
            </thead>
            <tbody>
              {imports.map((item, index) => (
                <tr
                  key={item.id}
                  className={`border-b border-border hover:bg-accent/30 ${
                    index % 2 === 1 ? "bg-muted" : ""
                  }`}
                >
                  <td
                    className="px-3 py-2 whitespace-nowrap"
                    title={`Recebido: ${formatDateTime(item.receivedAt)}`}
                  >
                    {formatDateTime(item.processedAt)}
                  </td>
                  <td className="px-3 py-2">{item.from || "—"}</td>
                  <td className="px-3 py-2">
                    {item.subject || "—"}
                    {item.error && (
                      <div className="text-xs mt-0.5" style={{ color: DANGER }}>
                        {item.error}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {item.attachmentCount}
                    {item.skippedAttachmentCount > 0 && (
                      <Tooltip
                        text={
                          item.skippedAttachmentNames
                            ? `Ficaram de fora: ${item.skippedAttachmentNames}`
                            : "Anexos recusados pelo filtro (tipo não aceite ou imagem pequena). Os nomes aparecem depois da próxima revisão automática."
                        }
                      >
                        <div
                          className="text-xs"
                          style={{ color: item.skippedDismissed ? "var(--secondary-text)" : "#b45309" }}
                        >
                          {item.skippedAttachmentCount === 1
                            ? "1 ignorado"
                            : `${item.skippedAttachmentCount} ignorados`}
                          {item.skippedDismissed && " · visto"}
                        </div>
                      </Tooltip>
                    )}
                    {item.skippedAttachmentCount > 0 && !item.skippedDismissed && (
                      <button
                        type="button"
                        className="text-xs hover:underline disabled:opacity-50"
                        style={{ color: "var(--primary)" }}
                        onClick={() => runDismissSkipped(item)}
                        disabled={dismissingKey === `email-${item.id}`}
                        title="O que ficou de fora não faz falta (logótipos, assinaturas…): deixa de contar no menu"
                      >
                        {dismissingKey === `email-${item.id}` ? "A dispensar…" : "Dispensar"}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">{item.importedCount}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    {item.hasEmail ? (
                      <button
                        className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-accent/30 disabled:opacity-50"
                        style={{ borderRadius: "var(--radius-button)" }}
                        onClick={() => openEmail(item)}
                        disabled={viewingId === item.id}
                        title="Ver conteúdo do email"
                      >
                        <Mail20Regular className="w-4 h-4" />
                        {viewingId === item.id ? "A abrir…" : "Ver"}
                      </button>
                    ) : (
                      <span className="text-secondary-text">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!isLoading && !loadError && discarded.length > 0 && (
          <div className="border-t border-border" ref={discardedSectionRef}>
            <div className="px-3 py-2 bg-muted/30 text-sm flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="font-medium">
                Descartados e falhados ({discarded.length}
                {reviewCounts && reviewCounts.discardedToReview > discarded.length
                  ? ` de ${reviewCounts.discardedToReview} — os mais recentes; dispensa ou recupera para ver os seguintes`
                  : ""}
                )
              </span>
              <span className="text-xs text-secondary-text">
                Anexos importados que não geraram fatura: o classificador não os reconheceu como
                fatura, ou o processamento falhou. Se algum for mesmo uma fatura ou um auto,
                recupera-o; os que não forem, dispensa-os e saem da lista.
              </span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-card border-b border-border">
                <tr className="text-left text-secondary-text">
                  <th className="px-3 py-2 font-medium">Ficheiro</th>
                  <th className="px-3 py-2 font-medium">Remetente</th>
                  <th className="px-3 py-2 font-medium">Assunto</th>
                  <th className="px-3 py-2 font-medium">Motivo</th>
                  <th className="px-3 py-2 font-medium text-center" style={{ width: 280 }}>
                    Ação
                  </th>
                </tr>
              </thead>
              <tbody>
                {discarded.map((doc, index) => (
                  <tr
                    key={doc.id}
                    className={`border-b border-border hover:bg-accent/30 ${
                      index % 2 === 1 ? "bg-muted" : ""
                    }`}
                  >
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-left hover:underline"
                        style={{ color: "var(--primary)" }}
                        title="Ver o documento"
                        onClick={() =>
                          setViewerDocument({
                            name: doc.fileName,
                            load: () => fetchFinancialDocument(doc.id),
                          })
                        }
                      >
                        <Eye20Regular className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                        <span>{doc.fileName}</span>
                      </button>
                    </td>
                    <td className="px-3 py-2">{doc.sourceSender || "—"}</td>
                    <td className="px-3 py-2">{doc.sourceSubject || "—"}</td>
                    <td className="px-3 py-2 text-xs text-secondary-text">
                      {doc.kind === "failed" && (
                        <span style={{ color: DANGER }}>Processamento falhou · </span>
                      )}
                      {doc.kind === "duplicate" && (
                        <span style={{ color: "#b45309" }}>Duplicado · </span>
                      )}
                      {doc.kind === "payment-proof" && (
                        <span style={{ color: "#0369a1" }}>Comprovativo · </span>
                      )}
                      {doc.motive || doc.classificationReason || "—"}
                    </td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <button
                        className="inline-flex items-center gap-1 px-2 py-1 border border-border rounded hover:bg-accent/30 disabled:opacity-50"
                        style={{ borderRadius: "var(--radius-button)" }}
                        onClick={() => runRecover(doc)}
                        disabled={recoveringId === doc.id || dismissingKey === `doc-${doc.id}`}
                        title={
                          doc.kind === "failed"
                            ? "Reprocessar o documento (fica por validar)"
                            : doc.kind === "duplicate"
                              ? "Importar mesmo sendo duplicado — cria a fatura na mesma (fica por validar)"
                              : doc.kind === "payment-proof"
                                ? "Afinal é uma fatura: marcar como fatura e reprocessar (fica por validar)"
                                : "Marcar como fatura e reprocessar (fica por validar)"
                        }
                      >
                        {recoveringId === doc.id
                          ? "A recuperar…"
                          : doc.kind === "failed"
                            ? "Tentar novamente"
                            : doc.kind === "duplicate"
                              ? "Importar mesmo assim"
                              : "Marcar como fatura"}
                      </button>
                      <button
                        className="inline-flex items-center gap-1 px-2 py-1 border border-border rounded hover:bg-accent/30 disabled:opacity-50"
                        style={{ borderRadius: "var(--radius-button)", marginLeft: 8 }}
                        onClick={() => runDismissDiscarded(doc)}
                        disabled={dismissingKey === `doc-${doc.id}` || recoveringId === doc.id}
                        title="Não é fatura nem auto: sai da lista e do número do menu (o ficheiro fica guardado)"
                      >
                        {dismissingKey === `doc-${doc.id}` ? "A dispensar…" : "Dispensar"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>

    {viewerDocument && (
      <DocumentViewer
        document={viewerDocument}
        onClose={() => setViewerDocument(null)}
      />
    )}

    {emailModal && (
      <Modal
        title={`Email — ${emailModal.item.subject || "(sem assunto)"}`}
        onClose={() => setEmailModal(null)}
        maxWidthClass="max-w-4xl"
      >
        <div className="text-sm space-y-0.5">
          <div>
            <span className="font-medium">De:</span> {emailModal.item.from || "—"}
          </div>
          <div>
            <span className="font-medium">Data:</span>{" "}
            {formatDateTime(emailModal.item.receivedAt)}
          </div>
        </div>
        {emailModal.error ? (
          <div className="p-4 text-center" style={{ color: "var(--destructive)" }}>
            {emailModal.error}
          </div>
        ) : (
          // Corpo do email renderizado num iframe totalmente isolado (sandbox vazio =
          // sem scripts, sem forms, origem opaca) — seguro para HTML não confiável.
          <iframe
            title="Conteúdo do email"
            sandbox=""
            srcDoc={emailModal.html ?? ""}
            style={{
              width: "100%",
              height: "60vh",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius)",
              background: "#ffffff",
            }}
          />
        )}
      </Modal>
    )}

    {confirmReprocess && (
      <Modal
        title="Reprocessar importações por email?"
        onClose={() => {
          if (!reprocessing) setConfirmReprocess(false);
        }}
        maxWidthClass="max-w-lg"
        footer={
          <>
            <button
              type="button"
              className="px-4 py-2 border border-border rounded transition-colors hover:bg-accent/30 disabled:opacity-50"
              style={{ borderRadius: "var(--radius-button)" }}
              onClick={() => setConfirmReprocess(false)}
              disabled={reprocessing}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="px-4 py-2 bg-primary text-primary-foreground rounded transition-colors disabled:opacity-50"
              style={{ borderRadius: "var(--radius-button)" }}
              onClick={runReprocess}
              disabled={reprocessing}
            >
              {reprocessing ? "A reprocessar…" : "Reprocessar"}
            </button>
          </>
        }
      >
        <p className="text-sm text-secondary-text">
          Vai voltar a correr a extração sobre os documentos importados por email e reclassificar
          despesa/receita com a lógica atual. Não recria documentos (sem duplicados) nem toca no
          Gmail. Registos já validados são ignorados.
        </p>
      </Modal>
    )}

    {confirmDismissAll && reviewCounts && (
      <Modal
        title="Dispensar todos os anexos ignorados?"
        onClose={() => {
          if (dismissingKey !== "email-all") setConfirmDismissAll(false);
        }}
        maxWidthClass="max-w-lg"
        footer={
          <>
            <button
              type="button"
              className="px-4 py-2 border border-border rounded transition-colors hover:bg-accent/30 disabled:opacity-50"
              style={{ borderRadius: "var(--radius-button)" }}
              onClick={() => setConfirmDismissAll(false)}
              disabled={dismissingKey === "email-all"}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="px-4 py-2 bg-primary text-primary-foreground rounded transition-colors disabled:opacity-50"
              style={{ borderRadius: "var(--radius-button)" }}
              onClick={runDismissAllSkipped}
              disabled={dismissingKey === "email-all"}
            >
              {dismissingKey === "email-all" ? "A dispensar…" : "Dispensar todos"}
            </button>
          </>
        }
      >
        <p className="text-sm">
          {reviewCounts.skippedToReview}{" "}
          {reviewCounts.skippedToReview === 1 ? "email deixa" : "emails deixam"} de contar no menu. Os
          anexos que ficaram de fora continuam a aparecer na linha de cada email (marcados como vistos).
          Os documentos descartados não mudam — esses decidem-se um a um.
        </p>
      </Modal>
    )}

    {confirmReset && (
      <Modal
        title="Apagar importações por email?"
        onClose={() => {
          if (!resetting) setConfirmReset(false);
        }}
        maxWidthClass="max-w-lg"
        footer={
          <>
            <button
              type="button"
              className="px-4 py-2 border border-border rounded transition-colors hover:bg-accent/30 disabled:opacity-50"
              style={{ borderRadius: "var(--radius-button)" }}
              onClick={() => setConfirmReset(false)}
              disabled={resetting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="px-4 py-2 text-white rounded transition-colors disabled:opacity-50"
              style={{ borderRadius: "var(--radius-button)", backgroundColor: "var(--destructive)" }}
              onClick={runReset}
              disabled={resetting}
            >
              {resetting ? "A apagar…" : "Apagar tudo"}
            </button>
          </>
        }
      >
        <p className="text-sm" style={{ color: "var(--destructive)" }}>
          Ação irreversível.
        </p>
        <p className="text-sm text-secondary-text">
          Apaga <strong>todos os documentos e faturas "por validar" importados por email</strong>,
          e o registo dessas importações — para se poder reimportar do zero. <strong>Registos já
          validados são mantidos</strong> (e não voltam a ser importados). Não mexe no Gmail: para
          reimportar tens de remover a etiqueta <code>AJA2/Importado</code> na caixa e reiniciar a API.
        </p>
      </Modal>
    )}
    </>
  );
}
